/**
 * The model-sheet plan — PRD 04 §7, ENGINEERING §7.
 *
 * A character is established by **one paid call that draws a whole grid of studies**, which
 * is then split for free. That sheet is the meet-your-character moment: the parent judges
 * the character from it, approves or remakes it, and on approval its cells populate the
 * starter matrix while one canonical crop becomes the base reference.
 *
 * This module is the **plan and the contract**, not the work. It decides which cells exist,
 * what variant each one is, and what counts as a usable split. The image-worker
 * (`engine/06`) draws the sheet and `sharp` cuts it up.
 *
 * The sheet is fixed in **shape** and derived in **content**: cell 0 is always the canonical
 * study, and cells 1..n-1 are the first points of `matrixPoints(axes)` in stable order — so
 * every populated cell is a **real, addressable matrix point** and the split can write
 * straight into the content-addressed cache.
 */
import { canonicalVariant, variantInstanceId } from './address.js';
import { canonicalJson } from './hash.js';
import { axisOptionLabel, matrixPoints, matrixSize } from './matrix.js';
import { DEFAULT_THEME_ID } from './theme.js';
export const SHEET_LAYOUTS = Object.freeze({
    'grid-2x2': { cols: 2, rows: 2, cells: 4, aspect: '1:1' },
    'grid-2x3': { cols: 2, rows: 3, cells: 6, aspect: '2:3' },
    'grid-3x3': { cols: 3, rows: 3, cells: 9, aspect: '1:1' },
});
export const DEFAULT_SHEET_LAYOUT = 'grid-2x3';
/** Smallest first, so "the smallest layout that fits" is a scan rather than a sort. */
const LAYOUTS_BY_SIZE = ['grid-2x2', 'grid-2x3', 'grid-3x3'];
/**
 * The smallest layout that holds the canonical study **plus every matrix point** — capped at
 * the largest sheet, floored at the smallest.
 *
 * Cell 0 always goes to the canonical study, so a sheet needs `matrixSize + 1` cells to hold
 * a whole matrix. Only a matrix larger than 8 points spills to the long tail.
 */
export function bestSheetLayoutFor(axes) {
    const wanted = Math.min(matrixSize(axes) + 1, SHEET_LAYOUTS['grid-3x3'].cells);
    return LAYOUTS_BY_SIZE.find((layout) => SHEET_LAYOUTS[layout].cells >= wanted) ?? 'grid-3x3';
}
/** 4K doubles both the split size and the price (ENGINEERING §7). */
export const SHEET_RESOLUTION_PX = Object.freeze({
    '2K': 2048,
    '4K': 4096,
});
export const DEFAULT_SHEET_RESOLUTION = '2K';
/**
 * How large each split pose comes out, so the Studio can say what a parent will get before
 * they pay for it. A 2K sheet on the default layout splits to roughly 680px poses, which is
 * what the spike measured (~600–840px).
 */
export function expectedCellPx(layout, resolution = DEFAULT_SHEET_RESOLUTION) {
    const spec = SHEET_LAYOUTS[layout];
    const longEdge = SHEET_RESOLUTION_PX[resolution];
    const [aspectW = 1, aspectH = 1] = spec.aspect.split(':').map(Number);
    const sheetWidth = aspectW >= aspectH ? longEdge : Math.round((longEdge * aspectW) / aspectH);
    const sheetHeight = aspectH >= aspectW ? longEdge : Math.round((longEdge * aspectH) / aspectW);
    return {
        width: Math.floor(sheetWidth / spec.cols),
        height: Math.floor(sheetHeight / spec.rows),
    };
}
/**
 * Plan the one paid call that establishes this character.
 *
 * Cell 0 is always the canonical study at the neutral variant — the crop that becomes the
 * base reference. Cells 1 onward take matrix points in stable order, skipping the neutral
 * point (cell 0 already is it), until the sheet runs out. Whatever is left over is the long
 * tail, generated on demand from the approved crop rather than drawn here.
 */
export function planModelSheet(primitive, options = {}) {
    const layout = options.layout ?? bestSheetLayoutFor(primitive.axes);
    const themeId = options.themeId ?? DEFAULT_THEME_ID;
    const cellCount = SHEET_LAYOUTS[layout].cells;
    const address = (variant) => variantInstanceId(primitive.id, primitive.version, variant, themeId);
    const cells = [
        { index: 0, role: 'canonical', variant: {}, instanceId: address({}) },
    ];
    const studies = matrixPoints(primitive.axes).filter((point) => Object.keys(point).length > 0);
    for (let index = 1; index < cellCount; index += 1) {
        const variant = studies[index - 1];
        cells.push(variant === undefined
            ? { index, role: 'unused' }
            : { index, role: 'variant', variant, instanceId: address(variant) });
    }
    return { layout, cells };
}
/** The populated cells, in sheet order — what the split expects to find. */
export function plannedCells(plan) {
    return plan.cells.filter((cell) => cell.role !== 'unused');
}
// ─── Instruction skeletons ───────────────────────────────────────────────────
/**
 * The canonical cell: the study that becomes the base reference, so it is deliberately
 * plain. Nothing about style or palette lives here — the theme owns style, and the prompt
 * composer owns the palette anchor.
 */
export const CANONICAL_CELL_INSTRUCTION = 'front-facing, whole figure from head to feet, feet visible, neutral pose and expression, arms relaxed at the sides, holding nothing';
/**
 * A short clause naming what varies in this cell — "pose: leaping · expression: sad".
 *
 * A skeleton only. The image-worker's prompt composer wraps it with the theme's style block
 * and the palette anchor; keeping those out of here is what stops two sources of style.
 */
export function cellInstruction(axes, variant) {
    const canonical = canonicalVariant(variant);
    const clauses = [];
    // Axis order, not key order: the sheet reads the way the axes were authored.
    for (const axis of axes) {
        const value = canonical[axis.key];
        if (value === undefined)
            continue;
        const optionLabel = axisOptionLabel(axes, axis.key, value)?.en ?? value;
        clauses.push(`${axis.label.en.toLowerCase()}: ${optionLabel.toLowerCase()}`);
    }
    return clauses.join(' · ');
}
// ─── The split contract (the pure half) ──────────────────────────────────────
/**
 * Per-cell quality gates, measured on the live spike run. A cell failing any of them is
 * skipped rather than shipped as a bad cut-out — the graceful fallback is to generate that
 * one pose on demand later.
 */
export const SPLIT_GATES = Object.freeze({
    /** How much larger than its neighbours a cell's subject may be before it is suspect. */
    maxCellSpan: 1.35,
    /** How much of a neighbour's pixels may intrude into this cell. */
    maxNeighbourIntrusion: 0.15,
    /** Below this the cell is effectively empty. */
    minFillRatio: 0.1,
    /** Below this the crop is too small to be a usable reference. */
    minEdgePx: 128,
});
/** Below this share of usable cells, the whole sheet is worth redrawing. */
export const SHEET_REMAKE_RATIO = 0.75;
function gateFailure(report) {
    if (report.fillRatio < SPLIT_GATES.minFillRatio) {
        return `cell is effectively empty (fill ${report.fillRatio.toFixed(2)} < ${SPLIT_GATES.minFillRatio})`;
    }
    if (report.span > SPLIT_GATES.maxCellSpan) {
        return `subject is out of scale with the sheet (span ${report.span.toFixed(2)} > ${SPLIT_GATES.maxCellSpan})`;
    }
    if (report.neighbourIntrusion > SPLIT_GATES.maxNeighbourIntrusion) {
        return `a neighbouring pose intrudes (${report.neighbourIntrusion.toFixed(2)} > ${SPLIT_GATES.maxNeighbourIntrusion})`;
    }
    if (report.edgePx < SPLIT_GATES.minEdgePx) {
        return `crop is too small to use (${report.edgePx}px < ${SPLIT_GATES.minEdgePx}px)`;
    }
    return null;
}
/**
 * Judge a split against the plan: which cells are usable, which are skipped and why, and
 * whether the whole sheet should be redrawn.
 *
 * The canonical cell is load-bearing — it becomes the base reference — so its failure alone
 * forces a remake however well the rest came out. Otherwise the sheet is remade only when
 * usable cells fall **below** `SHEET_REMAKE_RATIO`; exactly at the ratio it stands.
 */
export function decideSheetOutcome(plan, cellReports) {
    const reportByIndex = new Map(cellReports.map((report) => [report.index, report]));
    const planned = plannedCells(plan);
    const usable = [];
    const skipped = [];
    let canonicalFailed = false;
    for (const cell of planned) {
        const variantKey = canonicalJson(cell.variant);
        const report = reportByIndex.get(cell.index);
        const reason = report === undefined ? 'the splitter reported nothing for this cell' : gateFailure(report);
        if (reason === null) {
            usable.push(variantKey);
        }
        else {
            skipped.push({ variantKey, reason });
            if (cell.role === 'canonical')
                canonicalFailed = true;
        }
    }
    const expected = planned.length;
    const ratio = expected === 0 ? 0 : usable.length / expected;
    if (canonicalFailed) {
        return {
            usable,
            skipped,
            remakeRecommended: true,
            remakeReason: 'the canonical cell did not split cleanly, and it is the crop the whole character locks to',
        };
    }
    if (ratio < SHEET_REMAKE_RATIO) {
        return {
            usable,
            skipped,
            remakeRecommended: true,
            remakeReason: `only ${usable.length} of ${expected} cells split cleanly, below the ${SHEET_REMAKE_RATIO} threshold`,
        };
    }
    return { usable, skipped, remakeRecommended: false };
}
/**
 * How this primitive gets established. A character always gets a sheet: it is the
 * meet-your-character artifact, not merely an optimization. A reference drawing forces one
 * too, because the drawing is only ever read by the sheet call.
 */
export function establishmentModeFor(primitive) {
    if (primitive.establishment === 'model-sheet')
        return 'model-sheet';
    if (primitive.referenceDrawingPath !== undefined)
        return 'model-sheet';
    if (primitive.kind === 'character')
        return 'model-sheet';
    return 'single-pose';
}
//# sourceMappingURL=modelSheet.js.map